import { Injectable, signal } from '@angular/core';

import { Quantity, UnitSystem, formatMeasure } from './units';

const STORAGE_KEY = 'preferredUnits';
const DEFAULT_SYSTEM: UnitSystem = 'metric';

/**
 * The single choke point for the measurement-unit preference (metric vs
 * imperial), mirroring ThemeService/LanguageService. Metric is the default;
 * the choice is persisted to localStorage and restored on the next load.
 *
 * When a per-user backend field lands (e.g. /me.units), only this service
 * changes — every consumer reads the `system` signal.
 */
@Injectable({ providedIn: 'root' })
export class UnitService {
  private readonly systemSignal = signal<UnitSystem>(readStoredSystem());

  readonly system = this.systemSignal.asReadonly();

  /** TS-side formatting in the current system (templates use the measure pipe).
   *  Reads the signal, so callers inside computed()/templates stay reactive. */
  format(value: number | null | undefined, quantity: Quantity): string {
    return formatMeasure(value, quantity, this.system());
  }

  setSystem(system: UnitSystem): void {
    this.systemSignal.set(system);
    try {
      localStorage.setItem(STORAGE_KEY, system);
    } catch {
      // Storage may be unavailable (private mode / locked down). The choice
      // still applies for this session; it just won't be remembered.
    }
  }
}

function readStoredSystem(): UnitSystem {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'imperial' ? 'imperial' : DEFAULT_SYSTEM;
  } catch {
    return DEFAULT_SYSTEM;
  }
}
